import PageHeader from "@/components/PageHeader";
import CtaBand from "@/components/CtaBand";
import { MaskedLines, FadeUp, Overline } from "@/components/Reveal";

function Block({ num, title, children, testid }) {
  return (
    <div className="grid gap-10 border-t border-hairline py-16 lg:grid-cols-12" data-testid={testid}>
      <div className="lg:col-span-4">
        <FadeUp>
          <span className="font-mono text-sm tracking-[0.3em] text-gold">{num}</span>
          <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
            {title}
          </h2>
        </FadeUp>
      </div>
      <div className="space-y-5 text-lg leading-relaxed text-stone2 lg:col-span-8">{children}</div>
    </div>
  );
}

export default function OurStory() {
  return (
    <div>
      <PageHeader
        testid="story-header"
        overline="Our Story"
        lines={["Built around", "a simple idea."]}
        intro="Small-business owners deserve financial information they can actually understand and use."
      />

      {/* Narrative + portrait placeholder */}
      <section className="pb-24" data-testid="story-narrative-section">
        <div className="mx-auto grid max-w-7xl items-start gap-14 px-6 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <FadeUp>
              <p className="font-serif text-3xl italic leading-snug text-pine sm:text-4xl">
                "I'm a serial entrepreneur who evolved into a bookkeeper."
              </p>
            </FadeUp>
            <div className="mt-10 space-y-6 text-lg leading-relaxed text-stone2">
              <FadeUp delay={0.1}>
                <p>
                  My path here was shaped by years of owning businesses, managing rental
                  properties, working in construction estimating and project management, and
                  serving more than two decades in the military.
                </p>
              </FadeUp>
              <FadeUp delay={0.15}>
                <p>
                  Across all of those experiences, one thing remained consistent: good decisions
                  depend on good information.
                </p>
              </FadeUp>
              <FadeUp delay={0.2}>
                <p>
                  I understand what it is like to manage jobs, control costs, maintain equipment,
                  deal with customers, oversee properties, and handle the administrative side of
                  a business while still trying to keep the business moving forward.
                </p>
              </FadeUp>
              <FadeUp delay={0.25}>
                <p>
                  Over time, I became increasingly interested in the financial side of
                  business — not simply recording transactions, but understanding what the
                  numbers were saying. That eventually led to Arnold Bookkeeping, LLC.
                </p>
              </FadeUp>
            </div>
            <FadeUp delay={0.3}>
              <p className="mt-12 font-serif text-4xl italic text-pine">— Aaron Arnold</p>
              <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.3em] text-pine/60">
                Founder, Arnold Bookkeeping, LLC
              </p>
            </FadeUp>
          </div>
          <div className="lg:col-span-4">
            <FadeUp delay={0.15}>
              <div className="relative" data-testid="story-portrait">
                <div className="absolute -inset-3 rotate-2 rounded-2xl border border-gold/50" aria-hidden="true" />
                <img
                  src="https://customer-assets-jt897jd0.emergentagent.net/job_ledgerwise-20/artifacts/7eaf94c182494b01_1719691387521%20Headshot%20Photo.jpg"
                  alt="Aaron Arnold, founder of Arnold Bookkeeping"
                  className="relative aspect-[4/5] w-full rounded-2xl object-cover object-top shadow-2xl"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="bg-parchment py-24 lg:py-28" data-testid="story-blocks-section">
        <div className="mx-auto max-w-7xl px-6">
          <Block num="01" title="Built for Small-Business Owners" testid="story-block-built-for">
            <FadeUp>
              <p>
                Many business owners are excellent at what they do. Contractors know
                construction. Landscapers know properties and crews. Salon owners know their
                clients. Property owners know buildings and tenants.
              </p>
              <p>
                But being good at your business does not mean you want to spend your evenings
                reconciling accounts, sorting through QuickBooks, or trying to make sense of
                financial reports.
              </p>
              <p className="font-medium text-ink">
                My role is to make that side of the business easier to manage.
              </p>
            </FadeUp>
          </Block>

          <Block num="02" title="A Practical, Personal Approach" testid="story-block-approach">
            <FadeUp>
              <p>
                Arnold Bookkeeping is intentionally a small business serving other small
                businesses. When you work with Arnold Bookkeeping, you work directly with me. I
                learn how your business operates, what matters to you, and where you need
                support.
              </p>
              <p>
                I do not believe every business should be forced into the same package or
                process. A landscaping company, rental property owner, salon, contractor, and
                retail business can have very different accounting needs.
              </p>
              <p className="font-medium text-ink">
                The goal is to build a bookkeeping and tax relationship that fits the business
                rather than forcing the business to fit the bookkeeping.
              </p>
            </FadeUp>
          </Block>

          <div className="border-t border-hairline pt-16">
            <FadeUp>
              <Overline>03 — The purpose</Overline>
              <MaskedLines
                as="h2"
                inView
                lines={["Helping business owners", "understand their numbers."]}
                className="mt-5 max-w-3xl font-serif text-3xl font-medium leading-[1.1] tracking-tight text-ink sm:text-4xl lg:text-5xl"
              />
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-stone2">
                At the end of the day, my goal is not to turn business owners into accountants.
                It is to give them accurate information, fewer financial surprises, and greater
                confidence when making decisions about their businesses.
              </p>
              <p className="mt-6 max-w-2xl font-serif text-2xl italic leading-snug text-pine">
                That is the purpose behind Arnold Bookkeeping.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
